import { useFormikContext } from 'formik'
import React, { RefObject, useEffect, useRef } from 'react'
import { useLocation, useParams } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import {
  MasterTransaction,
  getTransactionRedirectForm,
  getTransactionRedirectUrl,
  isTransactionFormRedirect,
  isTransactionUrlRedirect,
} from '@model/MasterTransaction'

import { Loading } from '@shared/components/Loading/Loading'

import { transactionStorage } from '../../../../PaymentProviderRedirect/DepositRedirectFromProviderPage/DepositRedirectFromProviderPage'
import { getDepositBasePath } from '../DepositFlowLayout/helpers'
import type { DepositFormValues } from '../DepositFlowLayout/types'
import { DepositTarget } from '../hooks/types'

type LocationState = {
  transaction?: MasterTransaction
  values?: DepositFormValues
}

export const DepositRedirectPage: React.FC = () => {
  const navigate = useNavigate()

  const { target, id, walletId, pspId } = useParams<{
    target: DepositTarget
    id: string
    walletId?: string
    pspId?: string
  }>()

  const { values: formikValues } = useFormikContext<DepositFormValues>()
  const location = useLocation()
  const state = (location.state || {}) as LocationState

  const divRef = useRef<HTMLDivElement>(null)

  const resolvedTarget: DepositTarget =
    target ?? (formikValues.wallet ? 'wallet' : 'trading-account')

  const resolvedId =
    id ?? walletId ?? formikValues.wallet?.id ?? formikValues.tradingAccount?.id ?? ''

  const dashboardContext = formikValues.dashboardContext || 'traders-room'

  const basePath =
    resolvedTarget && resolvedId
      ? getDepositBasePath(resolvedTarget, resolvedId, dashboardContext, pspId)
      : `/dashboard/${dashboardContext}`

  useEffect(() => {
    if (!resolvedTarget || !resolvedId || !state.transaction) {
      navigate(`${basePath}/amount`, { replace: true })
      return
    }

    const tx = state.transaction
    const txValues = state.values ?? formikValues

    const redirectFailurePath = `${basePath}/transaction/failed`

    transactionStorage().addProviderDepositResponseTransaction({
      transaction: tx,
      values: txValues,
      match: {
        walletId: resolvedTarget === 'wallet' ? resolvedId : undefined,
        tradingAccountId: resolvedTarget === 'trading-account' ? resolvedId : undefined,
      },
      redirectPath: redirectFailurePath,
    })

    if (isTransactionUrlRedirect(tx)) {
      const url = getTransactionRedirectUrl(tx)
      if (url) {
        window.location.href = url
        return
      }

      navigate(redirectFailurePath, { replace: true })
      return
    }

    if (isTransactionFormRedirect(tx)) {
      const form = getTransactionRedirectForm(tx)
      if (form) {
        createRedirectForm(form, divRef)
        return
      }

      navigate(redirectFailurePath, { replace: true })
      return
    }

    navigate(`${basePath}/amount`, { replace: true })
  }, [
    basePath,
    dashboardContext,
    formikValues,
    navigate,
    pspId,
    resolvedId,
    resolvedTarget,
    state.transaction,
    state.values,
  ])

  return (
    <>
      <Loading isLoading showLoadingIcon />
      <div ref={divRef} />
    </>
  )
}

const createRedirectForm = (redirectForm: string, refObject: RefObject<HTMLDivElement | null>) => {
  const html = document.createRange().createContextualFragment(redirectForm)
  if (refObject.current) {
    refObject.current.innerHTML = ''
    refObject.current.appendChild(html)
  }
}
